import React from 'react';
import { hourlyEmployeesService } from '../../services/hourlyEmployeesService';
import ScrollIndicator from '../ScrollIndicator';

const EmployeeSummary = ({ 
  teamMembers = [], 
  reportType, 
  hourlyEmployeeHours = [],
  isEditing = false,
  onEmployeeTotalPayoutEdit = () => {},
  onResetEmployeeTotalPayout = () => {}
}) => {
  const isHousekeeping = reportType === 'HOUSEKEEPING';

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  // Separate team members into employees and contractors
  const employees = teamMembers.filter(member => 
    member.teamMemberId !== 0 && // Exclude unassigned jobs
    member.isContractor !== true
  );
  
  const contractors = teamMembers.filter(member => 
    member.teamMemberId !== 0 && // Exclude unassigned jobs
    member.isContractor === true
  );

  // Calculate totals for employees
  const employeeTotals = employees.reduce((totals, member) => ({
    jobs: totals.jobs + (member.jobsCount || 0),
    payout: totals.payout + (Number(member.totalPayout) || 0),
    billedAmount: totals.billedAmount + (Number(member.totalBilledAmount) || 0)
  }), { jobs: 0, payout: 0, billedAmount: 0 });

  // Calculate totals for contractors
  const contractorTotals = contractors.reduce((totals, member) => ({
    jobs: totals.jobs + (member.jobsCount || 0),
    payout: totals.payout + (Number(member.totalPayout) || 0),
    billedAmount: totals.billedAmount + (Number(member.totalBilledAmount) || 0)
  }), { jobs: 0, payout: 0, billedAmount: 0 });

  // Calculate totals for hourly employees (excluding contractors)
  const hourlyEmployeeTotals = hourlyEmployeeHours.reduce((totals, empHours) => {
    if (!empHours.hours || empHours.hours <= 0) return totals;
    // Exclude hourly contractors
    if (empHours.isContractor === true) return totals;
    
    // Get hourly rate from saved data or lookup (for backward compatibility)
    let hourlyRate = empHours.hourlyRate;
    if (!hourlyRate) {
      const employee = hourlyEmployeesService.getHourlyEmployee(empHours.employeeId);
      hourlyRate = employee?.hourlyRate || 0;
    }
    
    const pay = hourlyRate * parseFloat(empHours.hours);
    return {
      count: totals.count + 1,
      hours: totals.hours + parseFloat(empHours.hours),
      payout: totals.payout + pay
    };
  }, { count: 0, hours: 0, payout: 0 });

  // Calculate totals for hourly contractors
  const hourlyContractorTotals = hourlyEmployeeHours.reduce((totals, empHours) => {
    if (!empHours.hours || empHours.hours <= 0) return totals;
    // Only include hourly contractors
    if (empHours.isContractor !== true) return totals;
    
    // Get hourly rate from saved data or lookup (for backward compatibility)
    let hourlyRate = empHours.hourlyRate;
    if (!hourlyRate) {
      const employee = hourlyEmployeesService.getHourlyEmployee(empHours.employeeId);
      hourlyRate = employee?.hourlyRate || 0;
    }
    
    const pay = hourlyRate * parseFloat(empHours.hours);
    return {
      count: totals.count + 1,
      hours: totals.hours + parseFloat(empHours.hours),
      payout: totals.payout + pay
    };
  }, { count: 0, hours: 0, payout: 0 });

  // Don't render if no team members or hourly employees
  if (employees.length === 0 && contractors.length === 0 && hourlyEmployeeTotals.count === 0 && hourlyContractorTotals.count === 0) {
    return null;
  }

  // Process hourly employees data (non-contractors)
  const hourlyEmployeesData = hourlyEmployeeHours
    .filter(empHours => empHours.hours && empHours.hours > 0 && empHours.isContractor !== true)
    .map((empHours) => {
      // For backward compatibility: try to get data from saved report first, then fallback to service lookup
      let employeeName = empHours.name;
      let employeeRole = empHours.job;
      let hourlyRate = empHours.hourlyRate;
      
      // If name/job/rate not in saved data (old reports), look up from service
      if (!employeeName || !hourlyRate) {
        const employee = hourlyEmployeesService.getHourlyEmployee(empHours.employeeId);
        if (employee) {
          employeeName = employee.name;
          employeeRole = employee.job;
          hourlyRate = employee.hourlyRate;
        }
      }
      
      if (!employeeName) return null; // Skip if we can't find employee data
      
      const pay = hourlyRate * parseFloat(empHours.hours);
      
      return {
        id: empHours.employeeId,
        name: employeeName,
        role: employeeRole,
        hours: parseFloat(empHours.hours),
        payout: pay,
        billedAmount: 0 // Hourly employees don't have billed amounts
      };
    })
    .filter(Boolean);

  // Process hourly contractors data
  const hourlyContractorsData = hourlyEmployeeHours
    .filter(empHours => empHours.hours && empHours.hours > 0 && empHours.isContractor === true)
    .map((empHours) => {
      // For backward compatibility: try to get data from saved report first, then fallback to service lookup
      let employeeName = empHours.name;
      let employeeRole = empHours.job;
      let hourlyRate = empHours.hourlyRate;
      
      // If name/job/rate not in saved data (old reports), look up from service
      if (!employeeName || !hourlyRate) {
        const employee = hourlyEmployeesService.getHourlyEmployee(empHours.employeeId);
        if (employee) {
          employeeName = employee.name;
          employeeRole = employee.job;
          hourlyRate = employee.hourlyRate;
        }
      }
      
      if (!employeeName) return null; // Skip if we can't find employee data
      
      const pay = hourlyRate * parseFloat(empHours.hours);
      
      return {
        id: empHours.employeeId,
        name: employeeName,
        role: employeeRole,
        hours: parseFloat(empHours.hours),
        payout: pay,
        billedAmount: 0 // Hourly contractors don't have billed amounts
      };
    })
    .filter(Boolean);

  return (
    <div className="bg-white mb-6">
      <div className="lg:px-6 py-4">
        <h3 className="text-lg font-medium text-gray-900 px-2 lg:px-0">Employee Summary</h3>
      </div>

      <ScrollIndicator className="lg:px-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Jobs/Hours
              </th>
              {isHousekeeping && (
                <>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payout
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billed
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Employees Section */}
            {(employees.length > 0 || hourlyEmployeesData.length > 0) && (
              <>
                <tr className="bg-blue-50">
                  <td colSpan={isHousekeeping ? 5 : 3} className="px-6 py-2">
                    <span className="text-sm font-semibold text-gray-900">Employees</span>
                  </td>
                </tr>
                {employees.map((member) => (
                  <tr key={member.teamMemberId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {member.teamMemberName}
                      {member.usesSalaryCalculation && member.weeklySalary && (
                        <span className="ml-2 text-blue-600 font-medium">(Salaried)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.teamMemberRole || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.jobsCount} {member.jobsCount === 1 ? 'job' : 'jobs'}
                    </td>
                    {isHousekeeping && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end">
                              <input
                                type="number"
                                step="0.01"
                                value={member.totalPayout || 0}
                                onChange={(e) => onEmployeeTotalPayoutEdit(member.teamMemberId, e.target.value)}
                                className="w-28 px-2 py-1 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                              />
                              {member.isTotalPayoutOverridden && (
                                <div className="flex items-center ml-2">
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Override
                                  </span>
                                  <button
                                    onClick={() => onResetEmployeeTotalPayout(member.teamMemberId)}
                                    className="ml-1 text-xs text-gray-600 hover:text-gray-900 underline"
                                    title="Reset to calculated value"
                                  >
                                    Reset
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-end">
                              {formatCurrency(member.totalPayout)}
                              {member.isTotalPayoutOverridden && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Override
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(member.totalBilledAmount)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {/* Hourly Employees (Non-Contractor) */}
                {hourlyEmployeesData.map((employee) => (
                  <tr key={`hourly-${employee.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {employee.name}
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Hourly
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.role || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee.hours} {employee.hours === 1 ? 'hour' : 'hours'}
                    </td>
                    {isHousekeeping && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(employee.payout)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          -
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {/* Employee Subtotal */}
                <tr className="bg-blue-100 font-medium">
                  <td className="px-6 py-3 text-sm text-gray-900" colSpan="2">
                    Employees Subtotal
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                    {employeeTotals.jobs} {employeeTotals.jobs === 1 ? 'job' : 'jobs'}
                    {hourlyEmployeeTotals.hours > 0 && ` + ${hourlyEmployeeTotals.hours} hours`}
                  </td>
                  {isHousekeeping && (
                    <>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(employeeTotals.payout + hourlyEmployeeTotals.payout)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(employeeTotals.billedAmount)}
                      </td>
                    </>
                  )}
                </tr>
              </>
            )}

            {/* Contractors Section */}
            {(contractors.length > 0 || hourlyContractorsData.length > 0) && (
              <>
                <tr className="bg-green-50">
                  <td colSpan={isHousekeeping ? 5 : 3} className="px-6 py-2">
                    <span className="text-sm font-semibold text-gray-900">Contractors</span>
                  </td>
                </tr>
                {contractors.map((member) => (
                  <tr key={member.teamMemberId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {member.teamMemberName}
                      {member.usesSalaryCalculation && member.weeklySalary && (
                        <span className="ml-2 text-blue-600 font-medium">(Salaried)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.teamMemberRole || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.jobsCount} {member.jobsCount === 1 ? 'job' : 'jobs'}
                    </td>
                    {isHousekeeping && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {isEditing ? (
                            <div className="flex items-center justify-end">
                              <input
                                type="number"
                                step="0.01"
                                value={member.totalPayout || 0}
                                onChange={(e) => onEmployeeTotalPayoutEdit(member.teamMemberId, e.target.value)}
                                className="w-28 px-2 py-1 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                              />
                              {member.isTotalPayoutOverridden && (
                                <div className="flex items-center ml-2">
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Override
                                  </span>
                                  <button
                                    onClick={() => onResetEmployeeTotalPayout(member.teamMemberId)}
                                    className="ml-1 text-xs text-gray-600 hover:text-gray-900 underline"
                                    title="Reset to calculated value"
                                  >
                                    Reset
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-end">
                              {formatCurrency(member.totalPayout)}
                              {member.isTotalPayoutOverridden && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Override
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(member.totalBilledAmount)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {/* Hourly Contractors */}
                {hourlyContractorsData.map((employee) => (
                  <tr key={`hourly-contractor-${employee.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {employee.name}
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                        Hourly
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {employee.role || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee.hours} {employee.hours === 1 ? 'hour' : 'hours'}
                    </td>
                    {isHousekeeping && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(employee.payout)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          -
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {/* Contractor Subtotal */}
                <tr className="bg-green-100 font-medium">
                  <td className="px-6 py-3 text-sm text-gray-900" colSpan="2">
                    Contractors Subtotal
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                    {contractorTotals.jobs} {contractorTotals.jobs === 1 ? 'job' : 'jobs'}
                    {hourlyContractorTotals.hours > 0 && ` + ${hourlyContractorTotals.hours} hours`}
                  </td>
                  {isHousekeeping && (
                    <>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(contractorTotals.payout + hourlyContractorTotals.payout)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(contractorTotals.billedAmount)}
                      </td>
                    </>
                  )}
                </tr>
              </>
            )}


            {/* Grand Total */}
            <tr className="bg-blue-50 font-bold">
              <td className="px-6 py-4 text-sm text-gray-900" colSpan="2">
                Total
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {employeeTotals.jobs + contractorTotals.jobs} jobs
                {(hourlyEmployeeTotals.hours + hourlyContractorTotals.hours) > 0 && ` + ${hourlyEmployeeTotals.hours + hourlyContractorTotals.hours} hours`}
              </td>
              {isHousekeeping && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(employeeTotals.payout + contractorTotals.payout + hourlyEmployeeTotals.payout + hourlyContractorTotals.payout)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(employeeTotals.billedAmount + contractorTotals.billedAmount)}
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </ScrollIndicator>

      {/* Empty state if no assigned team members or hourly employees */}
      {employees.length === 0 && contractors.length === 0 && hourlyEmployeeTotals.count === 0 && (
        <div className="px-6 py-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No assigned team members</h3>
          <p className="mt-1 text-sm text-gray-500">
            This report contains only unassigned jobs or jobs without team member assignments.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployeeSummary;